import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from '../../../axios';
import { useNavigate } from 'react-router-dom';
import './RegisterForm.scss';
import logo from '../../../img/nekrodruk-logo-dark.png';
import hand from '../../../img/waving-hand-svgrepo-com.svg';
import { NavLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Collapse,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
} from '@mui/material';
import PasswordTextField from '../../PasswordTextField';
import { useAuth } from '../../../context/AuthContext';
import { getApiErrorMessage } from '../../../utils/notify';
import { PASSWORD_HINT, confirmPasswordSchema, emailSchema, passwordSchema } from '../../../utils/validation';

export default function RegisterForm() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const { isLoggedIn } = useAuth();

  // Opieramy się na zweryfikowanym logowaniu, a nie na samej obecności (być może nieważnego) tokenu
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/personForm');
    }
  }, [isLoggedIn, navigate]);

  const initialValues = {
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    address: '',
    phone: '',
    promoCode: '',
    acceptedTermsAndPrivacy: false,
  };

  const validationSchema = Yup.object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: confirmPasswordSchema('password'),
    name: Yup.string(),
    address: Yup.string(),
    phone: Yup.string().matches(/^[0-9]+$/, 'Numer telefonu musi składać się tylko z cyfr'),
    acceptedTermsAndPrivacy: Yup.boolean().oneOf(
      [true],
      'Musisz zaakceptować regulamin serwisu i politykę prywatności'
    ),
  });

  const [showPromo, setShowPromo] = useState(false);
  const [promoValidation, setPromoValidation] = useState({ status: 'idle', message: '' });
  const [lastCheckedPromoCode, setLastCheckedPromoCode] = useState('');

  const handleRegister = async (values, { setSubmitting }) => {
    const { email, password, name, address, phone, promoCode, acceptedTermsAndPrivacy } = values;
    const trimmedPromoCode = promoCode?.trim();
    setError('');
    if (
      trimmedPromoCode &&
      (promoValidation.status !== 'valid' || lastCheckedPromoCode !== trimmedPromoCode)
    ) {
      setError(
        promoValidation.status === 'invalid'
          ? promoValidation.message || 'Kod promocyjny jest nieprawidłowy.'
          : 'Sprawdź kod promocyjny przed rejestracją.'
      );
      setSubmitting(false);
      return;
    }
    try {
      await axios.post('/registerUser', {
        email,
        password,
        funeralhomename: name,
        funeralhomeaddress: address,
        tel: phone,
        promoCode: trimmedPromoCode || undefined,
        acceptedTerms: acceptedTermsAndPrivacy,
        acceptedPrivacy: acceptedTermsAndPrivacy,
      });
      setRegistered(true);
    } catch (error) {
      setError(getApiErrorMessage(error, 'Nie udało się założyć konta. Spróbuj ponownie.'));
    } finally {
      setSubmitting(false);
    }
  };

  if(registered) return (
    <Box className='RegisterForm'>
      <Paper elevation={0} className='formCard successCard'>
        <Stack spacing={3} alignItems='center'>
          <Box component='img' src={hand} alt='' className='hand_icon' />
          <Stack spacing={1} textAlign='center'>
            <Typography variant='h5' fontWeight={600}>Witaj!</Typography>
            <Typography variant='body1'>
              Konto zostało utworzone. Możesz się teraz zalogować.
            </Typography>
          </Stack>
          <Button
            component={NavLink}
            to='/login'
            variant='contained'
            size='large'
            className='formSubmit'
          >
            Przejdź do logowania
          </Button>
        </Stack>
      </Paper>
    </Box>
  );

  return (
    <Box className='RegisterForm'>
      <Paper elevation={0} className='formCard'>
        <Stack spacing={3} alignItems='center'>
          <Box component='img' src={logo} alt='Nekrodruk' className='logo' />
          <Typography component='h1' variant='h4' className='formTitle'>
            Załóż konto
          </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleRegister}
          >
            {({ isSubmitting }) => (
              <Form className='formContainer' noValidate>
                <Stack spacing={2} width='100%'>
                  <Field name='email'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='email'
                        label='Adres e-mail'
                        variant='outlined'
                        fullWidth
                        required
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Field name='password'>
                    {({ field, meta }) => (
                      <PasswordTextField
                        {...field}
                        label='Hasło'
                        variant='outlined'
                        fullWidth
                        required
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : PASSWORD_HINT}
                      />
                    )}
                  </Field>
                  <Field name='confirmPassword'>
                    {({ field, meta }) => (
                      <PasswordTextField
                        {...field}
                        label='Powtórz hasło'
                        variant='outlined'
                        fullWidth
                        required
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Typography variant='body2' className='register_info'>
                    Poniższe informacje są opcjonalne i służą wyłącznie do celów kontaktowych.
                  </Typography>
                  <Field name='name'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='text'
                        label='Nazwa zakładu'
                        variant='outlined'
                        fullWidth
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Field name='address'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='text'
                        label='Adres zakładu'
                        variant='outlined'
                        fullWidth
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Field name='phone'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='tel'
                        label='Telefon kontaktowy'
                        variant='outlined'
                        fullWidth
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Button
                    type='button'
                    variant='text'
                    className='promoLink'
                    onClick={() => setShowPromo(!showPromo)}
                  >
                    {showPromo ? 'Ukryj pole kodu promocyjnego' : 'Masz kod promocyjny?'}
                  </Button>
                  <Collapse in={showPromo}>
                    <Field name='promoCode'>
                      {({ field, form }) => {
                        const handleChange = (event) => {
                          const { value } = event.target;
                          form.setFieldValue(field.name, value);

                          const trimmedValue = value.trim();
                          if (!trimmedValue) {
                            setPromoValidation({ status: 'idle', message: '' });
                            setLastCheckedPromoCode('');
                            return;
                          }

                          setPromoValidation({ status: 'idle', message: '' });
                          setLastCheckedPromoCode('');
                        };

                        const handlePromoCheck = async () => {
                          const trimmedValue = field.value.trim();

                          if (!trimmedValue) {
                            setPromoValidation({ status: 'invalid', message: 'Kod nie może być pusty.' });
                            setLastCheckedPromoCode('');
                            return;
                          }

                          setPromoValidation({ status: 'checking', message: 'Sprawdzanie kodu…' });

                          try {
                            const response = await axios.post('/promocode/validate', { code: trimmedValue });
                            const successMessage = response.data?.message || 'Kod jest poprawny.';
                            setPromoValidation({ status: 'valid', message: successMessage });
                            setLastCheckedPromoCode(trimmedValue);
                          } catch (validationError) {
                            // Brak sieci / 500 / limit prób to nie to samo co błędny kod
                            const message = getApiErrorMessage(validationError, 'Kod jest nieprawidłowy.', {
                              404: 'Taki kod nie istnieje.',
                            });
                            setPromoValidation({ status: 'invalid', message });
                            setLastCheckedPromoCode('');
                          }
                        };

                        // Komunikat jako helperText pola – czytniki ekranu wiążą go z polem (aria-describedby)
                        return (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                            <TextField
                              {...field}
                              type='text'
                              label='Kod promocyjny'
                              variant='outlined'
                              fullWidth
                              value={field.value}
                              onChange={handleChange}
                              error={promoValidation.status === 'invalid'}
                              helperText={promoValidation.message || ' '}
                              FormHelperTextProps={promoValidation.status === 'valid' ? { sx: { color: 'success.main' } } : undefined}
                            />
                            <Button
                              type='button'
                              variant='outlined'
                              onClick={handlePromoCheck}
                              disabled={promoValidation.status === 'checking'}
                              sx={{ whiteSpace: 'nowrap', minHeight: 56 }}
                            >
                              {promoValidation.status === 'checking' ? 'Sprawdzanie…' : 'Sprawdź kod'}
                            </Button>
                          </Stack>
                        );
                      }}
                    </Field>
                  </Collapse>
                  <Stack spacing={1.5} alignItems='flex-start'>
                    <Field name='acceptedTermsAndPrivacy'>
                      {({ field, form, meta }) => (
                        <div className='consentField'>
                          <FormControlLabel
                            control={
                              <Checkbox
                                color='primary'
                                checked={field.value}
                                name={field.name}
                                onChange={(event) => form.setFieldValue(field.name, event.target.checked)}
                                onBlur={() => form.setFieldTouched(field.name, true)}
                                inputProps={{ 'aria-required': true }}
                              />
                            }
                            label={
                              <Typography variant='body2'>
                                Akceptuję{' '}
                                <Link href="/Regulamin_nekrodruk.pdf" target='_blank' rel='noopener noreferrer'>
                                  regulamin serwisu
                                </Link>
                                {' '}oraz{' '}
                                <Link href="/Polityka_prywatnosci_nekrodruk.pdf" target='_blank' rel='noopener noreferrer'>
                                  politykę prywatności
                                </Link>
                                <Box component='span' className='requiredAsterisk' aria-hidden='true'>
                                  {' '}*
                                </Box>
                              </Typography>
                            }
                          />
                          {meta.touched && meta.error ? (
                            <FormHelperText error>{meta.error}</FormHelperText>
                          ) : null}
                        </div>
                      )}
                    </Field>
                  </Stack>
                  {error && (
                    <Alert severity='error' className='formAlert'>
                      {error}
                    </Alert>
                  )}
                  <Button
                    type='submit'
                    variant='contained'
                    size='large'
                    disabled={isSubmitting}
                    className='formSubmit'
                  >
                    {isSubmitting ? 'Zakładanie konta…' : 'Załóż konto'}
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Paper>
    </Box>
  );
}
